import { type FunctionComponent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../../hooks/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { findErrorMessage, handleHttpError } from "../../../../utils/utils";
import { Form } from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { useInviteAdminHttp } from "../../../../hooks/http/admin/users/invite-admin.http";
import Spinner from "../../../../components/spinner.components";

const FormSchema = z.object({
  email: z.string().email(),
});

export const InviteAdminSliderAdminPartials: FunctionComponent = () => {
  const navigate = useNavigate();
  const { execute: inviteAdmin, fetching: invitingAdmin } =
    useInviteAdminHttp();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    const response = await inviteAdmin({ body: { email: data.email } });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    toast({ content: "Invitation sent successfully!", toastType: "success" });
    form.reset();
    return navigate(-1);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];

      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  return (
    <div className="flex flex-col gap-10 w-full h-full">
      <div className="flex items-center gap-5 w-full">
        <p className="font-semibold text-3xl">Invite Admin</p>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col justify-between items-center gap-10 w-full h-full"
        >
          <FormInput
            form={form}
            name="email"
            label="Enter email"
            placeholder="Email here...."
            required
            type="email"
          />
          <Button
            type="submit"
            className="flex gap-2 bg-primarybuttonbg py-8 w-full font-semibold text-primarybuttontext text-xl"
            disabled={invitingAdmin}
          >
            {invitingAdmin ? <Spinner width={15} /> : "Invite admin →"}
          </Button>
        </form>
      </Form>
    </div>
  );
};
